import { CheckIcon } from "@heroicons/react/20/solid";
import {
  useMeeting,
  useParticipant,
  usePubSub,
} from "@videosdk.live/react-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast, Bounce } from "react-toastify";
import useIsMobile from "../utils/useIsMobile";
import useIsTab from "../utils/useIsTab";
import useIsBigDesktop from "../utils/useIsBigScreen";
import { useAppContext } from "../AppContextDef";
import { useTranslation } from "react-i18next";
import { fontMap } from "../utils/common";
import usePubsubFunction from "../utils/usePubsubFunction";
import useFaceVerification from "../hooks/useFaceVerification";

const RemoteParticipantView = ({}) => {
  let isMobile = useIsMobile();
  let isTab = useIsTab();
  let isBigDesktop = useIsBigDesktop();
  const micRef = useRef(null);
  const videoRef = useRef(null);

  const {
    selectedLang,
    faceVerified,
    agreedData,
    faceDetectError,
    currentPageData,
    isAbortSignal,
    allPubsubData,
    delegatedApiSuccess,
    isMeetingEnded,
    recorderRefImageRef,
    setFaceVerified,
    setAgreedData,
    setFaceDetectError,
  } = useAppContext();

  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    changeLanguage(selectedLang?.lang);
  }, [selectedLang]);

  const { participants } = useMeeting({});
  const nonLocalParticipantId = Array.from(participants.values()).find(
    (p) => !p.local,
  )?.id;

  const {
    webcamStream,
    micStream,
    webcamOn,
    micOn,
    isLocal,
    displayName,
    captureImage,
    getVideoStats,
    getAudioStats,
  } = useParticipant(nonLocalParticipantId);

  // ---- recorder-owned verification engine --------------------------------
  const pubsubPublishMethod = usePubsubFunction();
  const { publish: faceDetectErrorPublish } = usePubSub(`FACE_DETECT_ERROR`);
  const { publish: faceVerifiedPublish } = usePubSub(`FACE_VERIFIED`);
  const { publish: agreedPublish } = usePubSub(`AGREED`);
  const { publish: abortSignalPublish } = usePubSub(`ABORT_SIGNAL`);
  const { publish: delegateApiPublish } = usePubSub(`DELEGATE_API`);

  const abortControllerRef = useRef(new AbortController());
  const isRequestInProgress = useRef(false);

  const faceVerification = useFaceVerification({
    mode: "owner",
    captureImage,
    webcamOn,
    token: allPubsubData?.videoSDKToken,
    roomId: allPubsubData?.videoSDKRoomId,
    abortSignalRef: abortControllerRef,
    currentPage: currentPageData,
    referenceImageRef: recorderRefImageRef,
    publishers: {
      pubsubPublishMethod,
      faceDetectErrorPublish,
      faceVerifiedPublish,
      agreedPublish,
      abortSignalPublish,
      delegateApiPublish,
    },
    callbacks: {
      onAgreed: (isAgreed) => {
        console.log(`[Recorder] verdict AGREED -> ${isAgreed}`);
        setAgreedData(isAgreed);
      },
      onVerified: (isVerified) => {
        console.log(`[Recorder] verdict FACE_VERIFIED -> ${isVerified}`);
        setFaceVerified({ isVerified, timestamp: Date.now() });
      },
      onError: (err) => {
        console.log(`[Recorder] verdict ERROR -> ${err}`);
        setFaceDetectError({ err, timestamp: Date.now() });
      },
      onFailure: (apiName, count) =>
        console.log(
          `[Recorder] "${apiName}" failed (consecutive failures: ${count}/3)`,
        ),
      onDelegate: (apiName) =>
        console.warn(
          `[Recorder] 3 failures on "${apiName}" — DELEGATING to client (pausing this API on recorder)`,
        ),
    },
  });

  // Keep the latest pipeline closure so the interval always runs fresh state.
  const pipelineRef = useRef(faceVerification.runOwnerPipeline);
  useEffect(() => {
    pipelineRef.current = faceVerification.runOwnerPipeline;
  });

  //TODO: Remove this interval after testing
  // const interval = setInterval(async () => {
  //   try {
  //     const videoStats = await getVideoStats();
  //     const audioStats = await getAudioStats();

  //     console.log("[RECORDER] video stats:", videoStats);
  //     console.log("[RECORDER] audio stats:", audioStats);

  //     console.log(
  //       `[RECORDER] video bitrate: ${videoStats} (low if < ${LOW_BITRATE_KBPS})`,
  //     );

  //   } catch (err) {
  //     console.error("[Client] failed to read video stats", err);
  //   }
  // }, 1000);

  // 5s verification loop, running off the customer's already-streamed frames.
  useEffect(() => {
    const ready =
      webcamOn &&
      webcamStream &&
      nonLocalParticipantId &&
      !isMeetingEnded &&
      allPubsubData?.videoSDKToken;

    console.log("[Recorder] verification loop gate", {
      page: currentPageData,
      webcamOn,
      hasStream: !!webcamStream,
      nonLocalParticipantId,
      isMeetingEnded,
      hasToken: !!allPubsubData?.videoSDKToken,
      ready: !!ready,
    });

    if (ready) {
      const makeRequest = () => {
        if (!isRequestInProgress.current) {
          isRequestInProgress.current = true;
          console.log(
            `[Recorder] running verification tick (page: ${currentPageData})`,
          );
          pipelineRef
            .current()
            .catch((err) =>
              console.error("[Recorder] verification tick failed", err),
            )
            .finally(() => (isRequestInProgress.current = false));
        } else {
          console.log("[Recorder] skipping tick — previous still in progress");
        }
      };

      if (currentPageData !== "PersonalDetails") {
        makeRequest();
      }

      console.log("[Recorder] verification loop STARTED (5s interval)");
      const intervalId = setInterval(makeRequest, 5000);
      return () => {
        console.log("[Recorder] verification loop STOPPED");
        clearInterval(intervalId);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    webcamOn,
    webcamStream,
    nonLocalParticipantId,
    isMeetingEnded,
    allPubsubData?.videoSDKToken,
    currentPageData,
  ]);

  // Handback: the client reported a successful fallback run for an API ->
  // reset that counter and resume owning the API on the next tick.
  useEffect(() => {
    if (delegatedApiSuccess?.apiName) {
      console.log(
        `[Recorder] client reported SUCCESS for delegated "${delegatedApiSuccess.apiName}" — handing back, recorder will resume this API`,
        delegatedApiSuccess,
      );
      faceVerification.handback(delegatedApiSuccess.apiName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delegatedApiSuccess]);

  // Cancel any in-flight owner requests when this view unmounts (page change /
  // meeting end). A fresh mount gets a fresh controller + counters.
  useEffect(() => {
    const controller = abortControllerRef.current;
    return () => {
      console.log("[Recorder] view unmounting — aborting in-flight requests");
      controller.abort();
    };
  }, []);

  const videoStream = useMemo(() => {
    if (webcamOn && webcamStream) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(webcamStream.track);
      return mediaStream;
    }
    return null;
  }, [webcamStream, webcamOn]);

  useEffect(() => {
    if (micRef.current) {
      if (micOn && micStream) {
        try {
          const mediaStream = new MediaStream();
          mediaStream.addTrack(micStream.track);
          micRef.current.srcObject = mediaStream;
          micRef.current
            .play()
            .catch((error) =>
              console.error("micRef.current.play() failed", error),
            );
        } catch (error) {
          console.error("Error handling mic stream:", error);
          micRef.current.srcObject = null;
        }
      } else {
        micRef.current.srcObject = null;
      }
    }
  }, [micStream, micOn]);

  useEffect(() => {
    if (videoRef.current) {
      if (webcamOn && videoStream) {
        try {
          videoRef.current.srcObject = videoStream;
          videoRef.current.setAttribute("playsinline", "");
          videoRef.current.setAttribute("x5-playsinline", "");
          videoRef.current.setAttribute("webkit-playsinline", "");
          videoRef.current
            .play()
            .catch((error) =>
              console.error("videoRef.current.play() failed", error),
            );
        } catch (error) {
          console.error("Error handling webcam stream:", error);
          videoRef.current.srcObject = null;
        }
      } else {
        videoRef.current.srcObject = null;
      }
    }
  }, [videoStream, webcamOn]);

  const fontFamilyCSS = fontMap[selectedLang?.lang] || "Lato";

  useEffect(() => {
    if (faceDetectError !== null) {
      let message =
        faceDetectError?.err === "NO_FACE_DETECTED"
          ? "No face detected. Please ensure your face is clearly visible."
          : faceDetectError?.err === "FACE_MATCH_UNSUCESSFUL"
            ? "Face Match Unsucessful!"
            : faceDetectError?.err === "MULTIPLE_FACES_DETECTED"
              ? "Multiple faces detected. Please make sure only one person is in view!"
              : faceDetectError?.err === "SPOOF_DETECTED"
                ? "Make sure a real person is in view and looking towards camera in a well-lit area!"
                : "";

      if (!isAbortSignal) {
        new Audio(`https://cdn.videosdk.live/icici/error_1.wav`).play();
        toast.error(`${t(message)}`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
          style: { fontFamily: fontFamilyCSS },
        });
      }
    }
  }, [faceDetectError, isAbortSignal]);

  return webcamOn ? (
    <div
      className={`flex md:flex-1 w-full flex-col items-center relative justify-center`}
    >
      {!faceVerified?.isVerified && (
        <>
          <div
            style={{
              height:
                !isMobile && !isTab && currentPageData !== "VideoConsent"
                  ? "550px"
                  : isTab
                    ? "290px"
                    : isBigDesktop
                      ? "600px"
                      : isMobile
                        ? "250px"
                        : "380px",
              width:
                isMobile || isTab
                  ? "100%"
                  : currentPageData === "VideoConsent"
                    ? isBigDesktop
                      ? "50%"
                      : "70%"
                    : "100%",
            }}
            className={`absolute  rounded-xl bg-[#16161680] z-30`}
          >
            {" "}
          </div>

          <div className="absolute z-40 flex flex-col items-center justify-center h-44 p-0">
            <p
              style={{ fontFamily: fontFamilyCSS }}
              className="text-white text-xl text-center mt-0 z-50"
            >
              {`${t("Please hold on while we verify your video")}...`}
            </p>
          </div>
        </>
      )}

      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={true}
        width={
          isMobile || isTab
            ? "100%"
            : currentPageData === "VideoConsent"
              ? isBigDesktop
                ? "50%"
                : "70%"
              : "100%"
        }
        className={
          faceVerified?.isVerified
            ? agreedData
              ? "border-2 border-green-600"
              : "border-2 border-red-700"
            : `relative ${
                currentPageData !== "VideoConsent" && "w-hull h-full"
              } `
        }
        // className="border border-green-150"
        style={{
          objectFit: "contain",
          borderRadius: 16,
          height:
            !isMobile && !isTab && currentPageData !== "VideoConsent"
              ? "550px"
              : isTab
                ? "290px"
                : isBigDesktop
                  ? "600px"
                  : isMobile
                    ? "250px"
                    : "380px",
          zIndex: 10,
        }}
      />
      <audio ref={micRef} autoPlay playsInline muted={false} />

      {faceVerified?.isVerified &&
        agreedData &&
        currentPageData === "VideoConsent" && (
          <div className="absolute top-0 right-2 xl:right-[150px] 2xl:right-[370px] z-10 bg-white p-1.5 md:p-2 mt-2 md:mt-3 flex items-center rounded-md max-w-max">
            <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-250" />
            <p
              style={{ fontFamily: fontFamilyCSS }}
              className="ml-2 text-green-250 text-sm md:text-base mb-0"
            >
              {t("Face detected")}
            </p>
          </div>
        )}

      {faceVerified?.isVerified && agreedData && isMobile && (
        <div className="absolute top-0 right-2 xl:right-[150px] 2xl:right-[520px] z-10 bg-white p-1.5 md:p-2 mt-2 md:mt-3 flex items-center rounded-md max-w-max">
          <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-250" />
          <p
            style={{ fontFamily: fontFamilyCSS }}
            className="ml-2 text-green-250 text-sm md:text-base mb-0"
          >
            {t("Face detected")}
          </p>
        </div>
      )}

      {faceVerified?.isVerified &&
        agreedData &&
        currentPageData !== "VideoConsent" &&
        !isMobile && (
          <div className="bg-[#3BA55D1A] p-1.5 md:p-2 mt-2 md:mt-3 flex items-center rounded-md max-w-max">
            <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-250" />
            <p
              style={{ fontFamily: fontFamilyCSS }}
              className="ml-2 text-green-250 text-sm md:text-base mb-0"
            >
              {t("Face detected")}
            </p>
          </div>
        )}
    </div>
  ) : null;
};

export default RemoteParticipantView;
